// ══════════════════════════════════════════════════════════════════════════════
// HomePage.js — VedSanga Home — Redesigned
// ══════════════════════════════════════════════════════════════════════════════
import { useState, useEffect } from "react";
import { LANG as PLANG, TITHI_TRANS, NAK_TRANS } from "./PanchangPage";

function trHome(enName, lang, table) {
  if (!enName) return "";
  if (lang === "en") return enName;
  return (table[lang] && table[lang][enName]) || enName;
}

// ── Rashi metadata (mirrors jyotish section) ──────────────────────────────────
const HOME_RASHIS = [
  {idx:0,en:"Aries",vedic:"Mesha",emoji:"♈",color:"#ef4444"},
  {idx:1,en:"Taurus",vedic:"Vrishabha",emoji:"♉",color:"#22c55e"},
  {idx:2,en:"Gemini",vedic:"Mithuna",emoji:"♊",color:"#6366f1"},
  {idx:3,en:"Cancer",vedic:"Karka",emoji:"♋",color:"#f97316"},
  {idx:4,en:"Leo",vedic:"Simha",emoji:"♌",color:"#f59e0b"},
  {idx:5,en:"Virgo",vedic:"Kanya",emoji:"♍",color:"#ec4899"},
  {idx:6,en:"Libra",vedic:"Tula",emoji:"♎",color:"#8b5cf6"},
  {idx:7,en:"Scorpio",vedic:"Vrischika",emoji:"♏",color:"#a855f7"},
  {idx:8,en:"Sagittarius",vedic:"Dhanu",emoji:"♐",color:"#14b8a6"},
  {idx:9,en:"Capricorn",vedic:"Makara",emoji:"♑",color:"#64748b"},
  {idx:10,en:"Aquarius",vedic:"Kumbha",emoji:"♒",color:"#06b6d4"},
  {idx:11,en:"Pisces",vedic:"Meena",emoji:"♓",color:"#3b82f6"},
];

// ── Rashi + Nakshatra Identity mini-card (no horoscope yet) ──────────────────
function RashiIdentityTeaser({ profile, onOpenJyotish }) {
  const rashi   = HOME_RASHIS[profile.rashi_idx];
  const nakName = profile.nakshatra?.name?.en || "";
  const nakSym  = profile.nakshatra?.symbol  || "⭐";
  if (!rashi) return null;

  const hexToRgb = h => {
    const r=parseInt(h.slice(1,3),16),g=parseInt(h.slice(3,5),16),b=parseInt(h.slice(5,7),16);
    return `${r},${g},${b}`;
  };
  const rgb = hexToRgb(rashi.color);

  return (
    <div onClick={onOpenJyotish} style={{
      background: "var(--bg2)",
      border: "1px solid var(--border)",
      borderRadius: "18px", boxShadow: "var(--card-shadow)",
      padding: "16px", marginBottom: "20px", cursor: "pointer",
      transition: "border-color 0.2s, box-shadow 0.2s",
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--saffron)"; e.currentTarget.style.boxShadow = "var(--card-shadow-hover)"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.boxShadow = "var(--card-shadow)"; }}
    >
      {/* Label row */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"10px" }}>
        <span style={{ fontSize:"0.95rem", fontFamily:"'Inter',sans-serif", fontWeight:700, color:"var(--saffron)" }}>⭐ Your Vedic Identity</span>
        <span style={{ background:"var(--bg3)", border:"1px solid var(--border)", color:"var(--text-dim)", borderRadius:20, padding:"2px 10px", fontSize:"0.8rem", fontFamily:"'Inter',sans-serif", fontWeight:600 }}>Free</span>
      </div>

      {/* Content row */}
      <div style={{ display:"flex", alignItems:"center", gap:"14px" }}>
        <div style={{ fontSize:"2.2rem", flexShrink:0 }}>
          {rashi.emoji}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"1.2rem", color:"var(--text)", fontWeight:700, marginBottom:2 }}>
            {rashi.vedic} · {rashi.en}
          </div>
          {nakName && (
            <div style={{ fontSize:"0.95rem", color:"var(--text-dim)" }}>
              {nakSym} {nakName}
            </div>
          )}
          {profile.archetype?.en && (
            <div style={{ fontSize:"0.92rem", color:"var(--saffron)", marginTop:"3px" }}>
              {profile.archetype.emoji} {profile.archetype.en} archetype
            </div>
          )}
        </div>
        <div style={{
          display:"flex", alignItems:"center", gap:4,
          background:"var(--bg2)", border:"1px solid var(--saffron)",
          borderRadius:10, padding:"7px 12px", flexShrink:0,
        }}>
          <span style={{ fontSize:"0.9rem", fontFamily:"'Inter',sans-serif", color:"var(--saffron)", fontWeight:700 }}>View</span>
          <span style={{ color:"var(--saffron)", fontSize:"1rem" }}>›</span>
        </div>
      </div>
    </div>
  );
}

// ── Rashi Horoscope teaser card ───────────────────────────────────────────────
function RashiHoroscopeTeaser({ userId, onOpenJyotish }) {
  const [data,    setData]    = useState(null);   // { rashi_idx, horoscope, profile }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    // Get rashi_idx from profile, then fetch today's horoscope
    fetch(`${API}/jyotish/profile/${encodeURIComponent(userId)}`)
      .then(r => r.json())
      .then(profile => {
        if (profile.error) { setLoading(false); return; }
        const idx = profile.rashi_idx;
        fetch(`${API}/horoscope/${idx}`)
          .then(r => r.json())
          .then(h => {
            setData({ rashi: HOME_RASHIS[idx], horoscope: h.error ? null : h, profile });
            setLoading(false);
          })
          .catch(() => {
            setData({ rashi: HOME_RASHIS[idx], horoscope: null, profile });
            setLoading(false);
          });
      })
      .catch(() => setLoading(false));
  }, [userId]);

  // No birth data at all — render nothing
  if (loading || !data) return null;

  // Has birth data but no horoscope — show identity mini-card as fallback
  if (!data.horoscope) {
    return <RashiIdentityTeaser profile={data.profile} onOpenJyotish={onOpenJyotish} />;
  }

  const { rashi, horoscope } = data;
  const hexToRgb = h => {
    const r=parseInt(h.slice(1,3),16),g=parseInt(h.slice(3,5),16),b=parseInt(h.slice(5,7),16);
    return `${r},${g},${b}`;
  };
  const accent = rashi.color;
  const rgb    = hexToRgb(accent);

  return (
    <div onClick={onOpenJyotish} style={{
      background: "var(--bg2)",
      border: "1px solid var(--border)",
      borderRadius: "20px", boxShadow: "var(--card-shadow)", padding: "22px 24px",
      marginBottom: "20px", cursor: "pointer",
      transition: "border-color 0.2s, box-shadow 0.2s",
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--saffron)"; e.currentTarget.style.boxShadow = "var(--card-shadow-hover)"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.boxShadow = "var(--card-shadow)"; }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "14px" }}>
        <div style={{ fontSize: "1.8rem" }}>
          {rashi.emoji}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: "0.95rem", color: "var(--saffron)", fontWeight: 700,
            fontFamily: "'Inter',sans-serif" }}>
            Today for your Rashi
          </div>
          <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1.05rem", color: "var(--text)" }}>
            {rashi.vedic} · {rashi.en}
          </div>
        </div>
        <div style={{ fontSize: "1rem", color: "var(--text-dim)" }}>›</div>
      </div>

      {/* Energy text */}
      {horoscope.energy && (
        <div style={{
          fontFamily: "'Inter',sans-serif", fontSize: "1.2rem",
          color: "var(--text)", lineHeight: 1.85, marginBottom: "12px",
          display: "-webkit-box", WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>
          {horoscope.energy}
        </div>
      )}

      {/* Verse */}
      {horoscope.verse && (
        <div style={{
          fontSize: "1.05rem", color: "var(--saffron)",
          fontFamily: "'Inter',sans-serif", fontStyle: "italic",
          borderLeft: "2px solid var(--saffron)", paddingLeft: "10px",
        }}>
          {horoscope.verse}
        </div>
      )}
    </div>
  );
}

const API = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");

function getGreeting() {
  const h = new Date().getHours();
  if (h >= 5  && h < 12) return { text: "Suprabhatam",   devanagari: "सुप्रभातम्"  };
  if (h >= 12 && h < 17) return { text: "Shubh Dopahar", devanagari: "शुभ दोपहर"   };
  if (h >= 17 && h < 21) return { text: "Shubh Sandhya", devanagari: "शुभ सन्ध्या" };
  return                         { text: "Namaste",       devanagari: "नमस्ते"       };
}

function getStreakMessage(streak) {
  if (streak <= 3)   return "A new habit is forming.";
  if (streak <= 6)   return "Consistency is dharma.";
  if (streak === 7)  return "One full week — the Gita calls this abhyasa.";
  if (streak === 14) return "Two weeks. You are not the same person who started.";
  if (streak <= 20)  return `${streak} days of showing up.`;
  return "21 days. You have become it.";
}

function formatDate() {
  return new Date().toLocaleDateString("en-IN", {
    weekday: "long", month: "long", day: "numeric",
  });
}

// ── Circular progress ring ────────────────────────────────────────────────────
function ProgressRing({ pct, size = 88, stroke = 4, color = "var(--saffron)" }) {
  const r   = (size - stroke * 2) / 2;
  const circ = 2 * Math.PI * r;
  const dash = circ * (1 - pct / 100);
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)", flexShrink: 0 }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none"
        stroke="rgba(255,255,255,0.06)" strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none"
        stroke={color} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={dash}
        strokeLinecap="round"
        style={{ transition: "stroke-dashoffset 0.8s ease" }} />
    </svg>
  );
}

// ── Community Stories ─────────────────────────────────────────────────────────
function StoriesFeed() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liked,   setLiked]   = useState({});

  useEffect(() => {
    fetch(`${API}/stories?limit=6`)
      .then(r => r.json())
      .then(d => { setStories(d.stories || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleLike = async (id) => {
    if (liked[id]) return;
    setLiked(prev => ({ ...prev, [id]: true }));
    try {
      const r = await fetch(`${API}/stories/${id}/like`, { method: "POST" });
      const d = await r.json();
      setStories(prev => prev.map(s => s.id === id ? { ...s, likes: d.likes } : s));
    } catch {}
  };

  if (loading) return null;
  if (!stories.length) return null;

  return (
    <div style={{ marginTop: "32px" }}>
      <div style={{
        fontFamily: "'Inter',sans-serif", fontWeight: 700,
        fontSize: "1.25rem", color: "var(--saffron)",
        marginBottom: "6px",
      }}>
        From the Community
      </div>
      <div style={{
        fontSize: "1.12rem", color: "var(--text-dim)",
        marginBottom: "16px",
        fontFamily: "'Inter',sans-serif",
      }}>
        Anonymous journeys completed — real shifts, real people
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {stories.map(s => {
          const delta = s.score_delta;
          const deltaColor = delta > 0 ? "#15803d" : delta < 0 ? "#b91c1c" : "var(--text-dim)";
          const deltaText  = delta > 0 ? `+${delta}` : `${delta}`;
          return (
            <div key={s.id} style={{
              background: "var(--bg2)",
              border: "1px solid var(--border)",
              borderRadius: "12px", boxShadow: "var(--card-shadow)",
              padding: "16px 20px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                <span style={{ fontSize: "1.4rem" }}>{s.journey_icon || "🌿"}</span>
                <div>
                  <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "0.95rem", color: "var(--saffron)" }}>
                    {s.journey_title}
                  </div>
                  <div style={{ fontSize: "0.92rem", color: "var(--text-dim)" }}>
                    {s.days_done} days · {s.first_emotion} → {s.last_emotion} ·{" "}
                    <span style={{ color: deltaColor, fontWeight: 600 }}>{deltaText} pts</span>
                  </div>
                </div>
              </div>

              {s.ai_reflection && (
                <div style={{
                  fontFamily: "'Inter',sans-serif",
                  fontSize: "1.2rem",
                  color: "var(--text)",
                  fontStyle: "italic",
                  lineHeight: "1.8",
                  marginBottom: "12px",
                  paddingLeft: "12px",
                  borderLeft: "2px solid var(--saffron)",
                }}>
                  "{s.ai_reflection}"
                </div>
              )}

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ fontSize: "0.92rem", color: "var(--text-dim)" }}>
                  {s.resonant_verse && `Most resonant: ${s.resonant_verse}`}
                </div>
                <button
                  onClick={() => handleLike(s.id)}
                  style={{
                    background: liked[s.id] ? "rgba(232,131,42,0.15)" : "transparent",
                    border: `1px solid ${liked[s.id] ? "var(--saffron)" : "var(--border)"}`,
                    borderRadius: "6px",
                    padding: "4px 10px",
                    cursor: liked[s.id] ? "default" : "pointer",
                    color: liked[s.id] ? "var(--saffron)" : "var(--text-dim)",
                    fontSize: "0.95rem",
                    display: "flex", alignItems: "center", gap: "4px",
                  }}
                >
                  🙏 {s.likes || 0}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Streak card (right column) ────────────────────────────────────────────────
function StreakCard({ streak }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      background: "var(--bg2)",
      border: "1px solid var(--border)",
      borderRadius: "16px", boxShadow: "var(--card-shadow)", padding: "24px 20px",
      marginBottom: "20px", textAlign: "center",
    }}>
      <div style={{ fontSize: "2.6rem",
        lineHeight: 1, marginBottom: "8px" }}>🔥</div>
      <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700,
        fontSize: "2.8rem", color: "var(--saffron)", lineHeight: 1 }}>{streak}</div>
      <div style={{ fontSize: "1rem", color: "var(--text-dim)",
        marginTop: "4px", fontWeight: 600 }}>
        {streak === 1 ? "Day" : "Days"}
      </div>
      <div style={{ fontSize: "0.95rem", color: "var(--text-dim)",
        marginTop: "8px", lineHeight: 1.5 }}>
        {getStreakMessage(streak)}
      </div>
    </div>
  );
}

// ── Hero section ──────────────────────────────────────────────────────────────
function HeroSection({ user }) {
  const greeting = getGreeting();
  return (
    <div style={{
      position: "relative", overflow: "hidden",
      padding: "36px 0 28px", marginBottom: "28px",
      borderBottom: "1px solid var(--border)",
    }}>
      {/* Om watermark — centered background */}
      <div style={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        fontFamily: "'Noto Sans Devanagari',serif",
        fontSize: "7.5rem", fontWeight: "bold",
        color: "rgba(232,131,42,0.38)",
        lineHeight: 1,
        textShadow: "0 0 60px rgba(232,131,42,0.4)",
        pointerEvents: "none", userSelect: "none",
      }}>ॐ</div>

      <div style={{ position: "relative" }}>
        <div style={{
          fontFamily: "'Inter',sans-serif", fontWeight: 700,
          fontSize: "clamp(1.9rem, 5vw, 2.6rem)",
          color: "var(--saffron)",
          lineHeight: 1.1, marginBottom: "8px",
        }}>
          {greeting.text}
        </div>
        <div style={{
          fontFamily: "'Inter',sans-serif",
          fontSize: "clamp(1.3rem, 3vw, 1.65rem)",
          color: "var(--text)", marginBottom: "8px", fontWeight: 600,
        }}>
          {user.name}
        </div>
        <div style={{ fontSize: "1.05rem", color: "var(--text-dim)" }}>
          {formatDate()}
        </div>
      </div>
    </div>
  );
}

// ── Journey hero card ─────────────────────────────────────────────────────────
function JourneyHeroCard({ journey, onGoToJourney }) {

  // No active journey
  if (!journey) {
    return (
      <div style={{
        background: "var(--bg2)",
        border: "1px solid var(--border)",
        borderRadius: "20px", boxShadow: "var(--card-shadow)", padding: "32px 28px",
        marginBottom: "20px",
      }}>
        <div style={{
          display: "inline-block",
          background: "var(--bg2)", border: "1px solid var(--saffron)",
          borderRadius: "20px", padding: "6px 16px",
          fontSize: "0.9rem", color: "var(--saffron)", fontWeight: 700,
          marginBottom: "18px",
        }}>21-Day Journey</div>

        <div style={{
          fontFamily: "'Inter',sans-serif", fontWeight: 700,
          fontSize: "1.6rem", color: "var(--text)",
          marginBottom: "14px", lineHeight: 1.3,
        }}>
          Your transformation waits
        </div>
        <div style={{
          fontFamily: "'Inter',sans-serif",
          fontSize: "1.25rem", color: "var(--text-dim)",
          lineHeight: 1.9, marginBottom: "28px",
        }}>
          Choose a struggle. The Gita gives you one verse a day for 21 days.
          Track how you feel. Watch yourself change.
        </div>

        <button onClick={onGoToJourney} style={{
          width: "100%", padding: "15px",
          background: "var(--saffron)",
          border: "none", borderRadius: "12px",
          color: "white", fontFamily: "'Inter',sans-serif", fontWeight: 700,
          fontSize: "1.05rem", cursor: "pointer",
          transition: "opacity 0.15s",
        }}
          onMouseEnter={e => { e.currentTarget.style.opacity = "0.88"; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
        >
          Begin your 21-day journey →
        </button>
      </div>
    );
  }

  const { icon, title, current_day, done_today, verse, days_done } = journey;
  const pct         = Math.round((days_done / 21) * 100);
  const shortMeaning = verse?.meaning
    ? verse.meaning.split(/[.!?]/)[0].trim()
    : "";

  return (
    <div style={{
      background: "var(--bg2)",
      border: "1px solid var(--border)",
      borderRadius: "20px", boxShadow: "var(--card-shadow)", padding: "24px 24px 20px",
      marginBottom: "20px",
    }}>
      {/* Label */}
      <div style={{
        display: "inline-block",
        background: "var(--bg2)", border: "1px solid var(--saffron)",
        borderRadius: "20px", padding: "6px 16px",
        fontSize: "0.9rem", color: "var(--saffron)", fontWeight: 700,
        marginBottom: "18px",
      }}>21-Day Journey</div>

      {/* Main row: progress ring + info */}
      <div style={{ display: "flex", alignItems: "center", gap: "20px", marginBottom: "18px" }}>
        {/* Ring with day number */}
        <div style={{ position: "relative", flexShrink: 0 }}>
          <ProgressRing pct={pct} size={88} stroke={4}
            color={done_today ? "#15803d" : "var(--saffron)"} />
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
          }}>
            {done_today ? (
              <div style={{ fontSize: "1.6rem", color: "#15803d" }}>✓</div>
            ) : (
              <>
                <div style={{
                  fontFamily: "'Inter',sans-serif", fontWeight: 700,
                  fontSize: "1.5rem", color: "var(--saffron)", lineHeight: 1,
                }}>{current_day}</div>
                <div style={{ fontSize: "0.85rem", color: "var(--text-dim)" }}>
                  of 21
                </div>
              </>
            )}
          </div>
        </div>

        {/* Journey info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
            <span style={{ fontSize: "1.4rem" }}>{icon}</span>
            <span style={{
              fontFamily: "'Inter',sans-serif", fontWeight: 700,
              fontSize: "1.1rem", color: "var(--text)",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{title}</span>
          </div>
          <div style={{ fontSize: "1.1rem", color: "var(--text-dim)", marginBottom: "8px" }}>
            {done_today
              ? `${days_done} days complete · come back tomorrow`
              : `${days_done} day${days_done !== 1 ? "s" : ""} done · ${21 - days_done} remaining`
            }
          </div>
          {/* Progress bar */}
          <div style={{ background: "var(--bg3)", borderRadius: "4px", height: "3px", overflow: "hidden" }}>
            <div style={{
              width: `${pct}%`, height: "100%",
              background: done_today ? "#15803d" : "var(--saffron)",
              borderRadius: "4px", transition: "width 0.8s ease",
            }} />
          </div>
        </div>
      </div>

      {/* Verse teaser */}
      {verse && shortMeaning && (
        <div style={{
          padding: "14px 16px",
          background: "var(--bg3)",
          border: "1px solid var(--border)",
          borderLeft: "3px solid var(--saffron)",
          borderRadius: "0 10px 10px 0",
          marginBottom: "18px",
        }}>
          <div style={{
            fontSize: "0.95rem", color: "var(--saffron)", fontWeight: 700, marginBottom: "8px",
          }}>
            BG {verse.chapter}.{verse.verse} — Today's Verse
          </div>
          <div style={{
            fontFamily: "'Inter',sans-serif",
            fontSize: "1.3rem", color: "var(--text)",
            fontStyle: "italic", lineHeight: 1.85,
          }}>
            "{shortMeaning}..."
          </div>
        </div>
      )}

      {/* CTA */}
      {done_today ? (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
          padding: "12px",
          background: "var(--bg3)", border: "1px solid var(--border)",
          borderRadius: "10px",
          color: "#15803d", fontSize: "0.95rem", fontWeight: 600,
        }}>
          ✓ Today's check-in complete
        </div>
      ) : (
        <button onClick={onGoToJourney} style={{
          width: "100%", padding: "14px",
          background: "var(--saffron)",
          border: "none", borderRadius: "12px",
          color: "white", fontFamily: "'Inter',sans-serif", fontWeight: 700,
          fontSize: "1rem", cursor: "pointer",
          transition: "opacity 0.15s",
        }}
          onMouseEnter={e => { e.currentTarget.style.opacity = "0.88"; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
        >
          Begin Day {current_day} →
        </button>
      )}
    </div>
  );
}

// ── Weekly insight ────────────────────────────────────────────────────────────
function WeeklyInsightCard({ insight, onOpenCompanion }) {
  const [expanded, setExpanded] = useState(false);
  const preview = insight.text.slice(0, 130) + (insight.text.length > 130 ? "..." : "");

  return (
    <div style={{
      background: "var(--bg2)",
      border: "1px solid var(--border)",
      borderRadius: "20px", boxShadow: "var(--card-shadow)", padding: "22px 24px",
      marginBottom: "20px",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "14px" }}>
        <div style={{ fontSize: "1.5rem" }}>🌙</div>
        <div>
          <div style={{ fontSize: "0.95rem", color: "var(--saffron)", fontWeight: 700 }}>
            Week {insight.week} Insight
          </div>
          <div style={{ fontSize: "0.92rem", color: "var(--text-dim)" }}>
            After {insight.day} days
          </div>
        </div>
      </div>

      <div style={{
        fontFamily: "'Inter',sans-serif",
        fontSize: "1.15rem", lineHeight: 1.85, color: "var(--text)",
        marginBottom: "16px",
      }}>
        {expanded ? insight.text : preview}
      </div>

      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
        {insight.text.length > 130 && (
          <button onClick={() => setExpanded(e => !e)} style={{
            background: "none", border: "1px solid var(--border)",
            borderRadius: "8px", padding: "7px 16px",
            color: "var(--saffron)", fontFamily: "'Inter',sans-serif", fontSize: "1rem",
            cursor: "pointer",
          }}>
            {expanded ? "Show less" : "Read more"}
          </button>
        )}
        <button onClick={onOpenCompanion} style={{
          background: "var(--bg2)", border: "1px solid var(--saffron)",
          borderRadius: "8px", padding: "7px 16px",
          color: "var(--saffron)", fontFamily: "'Inter',sans-serif", fontSize: "1rem",
          cursor: "pointer",
        }}>
          🕯️ Continue with companion
        </button>
      </div>
    </div>
  );
}

// ── Quick ask bar ─────────────────────────────────────────────────────────────
function QuickAskBar({ lastTopic, onAsk }) {
  const [text, setText] = useState("");
  const [focused, setFocused] = useState(false);

  const handle = () => { if (text.trim()) onAsk(text.trim()); };

  return (
    <div style={{
      background: "linear-gradient(135deg,var(--bg2),var(--bg3))",
      border: `1px solid ${focused ? "rgba(232,131,42,0.5)" : "var(--border)"}`,
      borderRadius: "20px", padding: "20px 22px",
      marginBottom: "20px",
      boxShadow: focused ? "0 0 32px rgba(232,131,42,0.08)" : "none",
      transition: "border-color 0.2s, box-shadow 0.2s",
      position: "relative", overflow: "hidden",
    }}>
      <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:"linear-gradient(90deg,#E8832A,#FFD060,#C9953A)" }} />
      <div style={{
        fontFamily: "'Cinzel Decorative',serif",
        fontSize: "1.15rem", color: "var(--saffron)",
        letterSpacing: "0.06em",
        marginBottom: "12px",
      }}>Ask the Gita</div>

      {lastTopic && !text && (
        <div style={{
          fontSize: "1rem", color: "var(--text-dim)", fontStyle: "italic",
          marginBottom: "10px",
        }}>
          Last time: "{lastTopic}"
        </div>
      )}

      <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "12px" }}>
        <input
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handle()}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder="What's weighing on you today?"
          style={{
            flex: 1, background: "#FAFAFA",
            border: "1px solid var(--border)", borderRadius: "10px",
            padding: "12px 16px", color: "var(--text)",
            fontFamily: "'Crimson Pro',serif", fontSize: "1.15rem",
            outline: "none", transition: "border-color 0.2s",
          }}
          onFocusCapture={e => { e.target.style.borderColor = "rgba(232,131,42,0.5)"; }}
          onBlurCapture={e =>  { e.target.style.borderColor = "var(--border)"; }}
        />
        <button onClick={handle} disabled={!text.trim()} style={{
          width: "44px", height: "44px", borderRadius: "50%", border: "none", flexShrink: 0,
          background: text.trim()
            ? "linear-gradient(135deg,var(--saffron),var(--gold))"
            : "var(--bg3)",
          color: text.trim() ? "white" : "var(--text-dim)",
          cursor: text.trim() ? "pointer" : "default",
          fontSize: "1.1rem", transition: "all 0.2s",
          boxShadow: text.trim() ? "0 4px 16px rgba(232,131,42,0.35)" : "none",
        }}>🙏</button>
      </div>

      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        {["Why do I feel lost?", "How do I let go?", "What is my purpose?"].map(q => (
          <button key={q} onClick={() => onAsk(q)} style={{
            background: "rgba(232,131,42,0.06)", border: "1px solid rgba(232,131,42,0.18)",
            borderRadius: "20px", padding: "6px 15px",
            color: "var(--text-dim)", fontFamily: "'Crimson Pro',serif", fontSize: "0.97rem",
            cursor: "pointer", transition: "all 0.2s",
          }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(232,131,42,0.15)"; e.currentTarget.style.color = "var(--saffron)"; e.currentTarget.style.borderColor = "rgba(232,131,42,0.4)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(232,131,42,0.06)"; e.currentTarget.style.color = "var(--text-dim)"; e.currentTarget.style.borderColor = "rgba(232,131,42,0.18)"; }}
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Stats strip ───────────────────────────────────────────────────────────────
function StatsStrip({ totalConvs, daysOnJourney }) {
  const stats = [
    { label: "Conversations", value: totalConvs || 0,    icon: "💬" },
    { label: "Journey Days",  value: daysOnJourney || 0, icon: "🌟" },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "16px" }}>
      {stats.map(s => (
        <div key={s.label} style={{
          background: "var(--bg2)", border: "1px solid var(--border)",
          borderRadius: "16px", boxShadow: "var(--card-shadow)", padding: "14px 18px",
          display: "flex", alignItems: "center", gap: "14px",
        }}>
          <div style={{ fontSize: "1.4rem", flexShrink: 0 }}>{s.icon}</div>
          <div>
            <div style={{
              fontFamily: "'Inter',sans-serif", fontWeight: 700,
              fontSize: "2rem", color: "var(--saffron)", lineHeight: 1,
            }}>
              {s.value}
            </div>
            <div style={{
              fontSize: "0.95rem", color: "var(--text-dim)",
              marginTop: "4px",
            }}>
              {s.label}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
function Skeleton({ h = 80, radius = 16 }) {
  return (
    <div style={{
      height: h, borderRadius: radius,
      background: "linear-gradient(90deg,var(--bg2) 25%,var(--bg3) 50%,var(--bg2) 75%)",
      backgroundSize: "200% 100%",
      animation: "shimmer 1.4s infinite",
      marginBottom: 16,
    }} />
  );
}

// ── AI Pandit free trial card ─────────────────────────────────────────────────
const AI_PANDIT_EXAMPLES = [
  "Why is my career stuck despite working so hard?",
  "When will my marriage happen?",
  "Will this relationship work for me?",
  "Why do I keep losing money?",
  "Is this the right time to start my business?",
];
const AI_PANDIT_STORAGE_KEY = "vp_aiPandit_freeCount";

function AIPanditCard({ user, onOpenJyotish }) {
  const [text,     setText]     = useState("");
  const [focused,  setFocused]  = useState(false);
  const [answer,   setAnswer]   = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [freeLeft, setFreeLeft] = useState(() => {
    const used = parseInt(localStorage.getItem(AI_PANDIT_STORAGE_KEY) || "0", 10);
    return Math.max(0, 3 - used);
  });

  const ask = async (q) => {
    const question = (q || text).trim();
    if (!question || loading) return;
    if (freeLeft <= 0) { onOpenJyotish && onOpenJyotish(); return; }
    setLoading(true); setAnswer(null);
    try {
      const res = await fetch(`${API}/jyotish/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: user?.id || "", message: question, history: [] }),
      });
      const d = await res.json();
      if (d.reply && !d.error) {
        setAnswer(d.reply);
        const newUsed = parseInt(localStorage.getItem(AI_PANDIT_STORAGE_KEY) || "0", 10) + 1;
        localStorage.setItem(AI_PANDIT_STORAGE_KEY, String(newUsed));
        setFreeLeft(Math.max(0, 3 - newUsed));
      } else {
        setAnswer(d.reply || "Please add your birth details in the Stars tab to receive your personal reading.");
      }
    } catch {
      setAnswer("Unable to reach Vedic Pandit right now. Please try again in a moment.");
    }
    setLoading(false); setText("");
  };

  return (
    <div style={{
      background: "var(--bg2)",
      border: `1px solid ${focused ? "var(--saffron)" : "var(--border)"}`,
      borderRadius: "20px", marginBottom: "20px",
      boxShadow: focused ? "var(--card-shadow-hover)" : "var(--card-shadow)",
      transition: "border-color 0.2s, box-shadow 0.2s",
    }}>
      <div style={{ padding:"22px 22px 24px" }}>

        {/* Header */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
          <div style={{
            fontFamily:"'Inter',sans-serif", fontSize:"1.05rem",
            color:"var(--saffron)", fontWeight:700,
          }}>
            🔱 Vedic Pandit
          </div>
          <div style={{
            background:"var(--bg2)", border:"1px solid var(--saffron)",
            borderRadius:20, padding:"4px 14px",
            fontFamily:"'Inter',sans-serif", fontSize:"0.8rem",
            color:"var(--saffron)", fontWeight:700,
            whiteSpace:"nowrap", flexShrink:0, marginLeft:10,
          }}>
            Free trial
          </div>
        </div>

        {/* Sacred invitation */}
        <div style={{
          fontFamily:"'Inter',sans-serif", fontSize:"1.25rem",
          color:"var(--text)", lineHeight:1.5, marginBottom:18, fontWeight:500,
        }}>
          What weighs on your heart today?
        </div>

        {/* Example chips */}
        {!answer && !loading && (
          <div style={{ display:"flex", gap:"8px", flexWrap:"wrap", marginBottom:18 }}>
            {AI_PANDIT_EXAMPLES.map((ex,i) => (
              <button key={i} onClick={() => ask(ex)} disabled={freeLeft <= 0} style={{
                background:"var(--bg2)",
                border:"1px solid var(--border)",
                borderRadius:24, padding:"8px 16px",
                color:"var(--text-dim)",
                fontFamily:"'Inter',sans-serif", fontSize:"0.95rem", fontWeight:500,
                cursor: freeLeft > 0 ? "pointer" : "default",
                transition:"all 0.2s", lineHeight:1.3,
                textAlign:"left",
              }}
                onMouseEnter={e => { if(freeLeft>0){ e.currentTarget.style.borderColor="var(--saffron)"; e.currentTarget.style.color="var(--saffron)"; } }}
                onMouseLeave={e => { e.currentTarget.style.borderColor="var(--border)"; e.currentTarget.style.color="var(--text-dim)"; }}
              >{ex}</button>
            ))}
          </div>
        )}

        {/* Answer */}
        {answer && (
          <div style={{
            background:"var(--bg3)",
            border:"1px solid var(--border)",
            borderLeft:"3px solid var(--saffron)",
            borderRadius:"0 16px 16px 0",
            padding:"18px 20px", marginBottom:18,
          }}>
            <div style={{
              fontFamily:"'Inter',sans-serif", fontSize:"1.1rem",
              color:"var(--text)", lineHeight:1.85,
            }}>{answer}</div>
          </div>
        )}

        {/* Input */}
        {freeLeft > 0 ? (
          <div>
            <div style={{ position:"relative" }}>
              <input
                value={text} onChange={e => setText(e.target.value)}
                onKeyDown={e => e.key === "Enter" && ask()}
                onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
                placeholder="Ask about career, marriage, health, timing…"
                style={{
                  width:"100%", boxSizing:"border-box",
                  background: focused ? "#FFF7EF" : "rgba(232,131,42,0.05)",
                  border:`1.5px solid ${focused ? "var(--saffron)" : "rgba(232,131,42,0.45)"}`,
                  borderRadius:16, padding:"17px 58px 17px 20px",
                  color:"var(--text)",
                  fontFamily:"'Inter',sans-serif",
                  fontSize:"1.15rem", fontWeight:500,
                  outline:"none",
                  boxShadow: focused ? "0 0 0 3px rgba(232,131,42,0.12)" : "none",
                  transition:"border-color 0.2s, background 0.2s, box-shadow 0.2s",
                }}
              />
              <button
                onClick={() => ask()}
                disabled={!text.trim() || loading}
                style={{
                  position:"absolute", right:8, top:"50%", transform:"translateY(-50%)",
                  width:42, height:42, borderRadius:"50%", flexShrink:0,
                  background: text.trim() && !loading ? "var(--saffron)" : "var(--bg3)",
                  border: "none",
                  color: text.trim() && !loading ? "#fff" : "var(--text-dim)",
                  cursor: text.trim() && !loading ? "pointer" : "default",
                  fontSize:"1.1rem",
                  transition:"all 0.2s",
                  display:"flex", alignItems:"center", justifyContent:"center",
                }}
              >
                {loading
                  ? <span style={{fontSize:"1rem",fontFamily:"'Inter',sans-serif"}}>•••</span>
                  : "↑"
                }
              </button>
            </div>
            <div style={{
              marginTop:10, textAlign:"center",
              fontFamily:"'Inter',sans-serif", fontSize:"0.9rem",
              color:"var(--text-dim)",
            }}>
              {freeLeft} free question{freeLeft>1?"s":""} left · no sign-up needed
            </div>
          </div>
        ) : (
          <button onClick={onOpenJyotish} style={{
            width:"100%",
            background:"var(--saffron)",
            border:"none", borderRadius:16, padding:"15px",
            color:"white", fontFamily:"'Inter',sans-serif",
            fontSize:"1rem", fontWeight:700, cursor:"pointer",
            transition:"opacity 0.2s",
          }}
            onMouseEnter={e => { e.currentTarget.style.opacity = "0.88"; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
          >Ask more every day — Guru Kripa ✦</button>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN
// ══════════════════════════════════════════════════════════════════════════════

// ── Daily personalized briefing card ─────────────────────────────────────────
function DailyBriefingCard({ userId, onOpenJyotish, lang: langProp, scriptPref: scriptPropPref }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    const baseLang   = langProp   || localStorage.getItem("panchang_lang") || "en";
    const scriptPref = scriptPropPref || localStorage.getItem("jyotish_script_pref") || "native";
    const lang = baseLang === "ta" ? (scriptPref === "roman" ? "ta_roman" : "ta") : "en";
    fetch(`${API}/jyotish/daily-briefing/${encodeURIComponent(userId)}?lang=${lang}`)
      .then(r => r.json())
      .then(d => { setData(d.error ? null : d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [userId, langProp, scriptPropPref]);

  // No birth data — nudge to fill it in
  if (!loading && !data) {
    return (
      <div onClick={onOpenJyotish} style={{
        background: "var(--bg2)",
        border: "1px solid var(--border)",
        borderRadius: "18px", boxShadow: "var(--card-shadow)", padding: "20px 22px", marginBottom: "20px",
        cursor: "pointer", display: "flex", alignItems: "center", gap: "14px",
        transition: "border-color 0.2s, box-shadow 0.2s",
      }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--saffron)"; e.currentTarget.style.boxShadow = "var(--card-shadow-hover)"; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.boxShadow = "var(--card-shadow)"; }}
      >
        <div style={{ fontSize: "1.6rem", width: 48, height: 48, borderRadius: "50%",
          background: "var(--bg3)", border: "1px solid var(--border)",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>🔮</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700,
            fontSize: "0.95rem", color: "var(--saffron)", marginBottom: "4px" }}>
            Today · Your Briefing
          </div>
          <div style={{ fontSize: "1.2rem", color: "var(--text)", lineHeight: 1.6,
            fontFamily: "'Inter',sans-serif" }}>
            Add your birth date to get a personalized daily reading — dasha, hora, remedy.
          </div>
        </div>
        <div style={{ color: "var(--text-dim)", fontSize: "1.1rem", flexShrink: 0 }}>›</div>
      </div>
    );
  }

  if (loading || !data) return null;

  const { rashi, nakshatra, current_hora, dasha, shani_alert,
          tithi, festival, urgent_message, remedy_today } = data;

  const isUrgent = !!shani_alert;
  const alertColor = "#b91c1c";

  return (
    <div onClick={onOpenJyotish} style={{
      background: "var(--bg2)",
      border: `1px solid ${isUrgent ? alertColor : "var(--border)"}`,
      borderLeft: `4px solid ${isUrgent ? alertColor : "var(--saffron)"}`,
      borderRadius: "20px", boxShadow: "var(--card-shadow)", marginBottom: "20px",
      overflow: "hidden", cursor: "pointer",
      transition: "border-color 0.2s, box-shadow 0.2s",
    }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = "var(--card-shadow-hover)"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "var(--card-shadow)"; }}
    >
      <div style={{ padding: "20px 22px 22px" }}>
        {/* Header row */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontSize: "1.6rem" }}>{rashi?.emoji}</span>
            <div>
              <div style={{
                fontSize: "0.95rem",
                color: isUrgent ? alertColor : "var(--saffron)",
                fontFamily: "'Inter',sans-serif", fontWeight: 700,
              }}>
                {isUrgent ? "⚠ Alert" : "Today · Your Briefing"}
              </div>
              <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1.05rem", color: "var(--text)" }}>
                {rashi?.vedic} · {nakshatra}
              </div>
            </div>
          </div>
          <div style={{ fontSize: "1rem", color: "var(--text-dim)" }}>›</div>
        </div>

        {/* Urgent message */}
        <div style={{
          fontFamily: "'Inter',sans-serif",
          fontSize: "1.2rem", color: "var(--text)", lineHeight: 1.85,
          marginBottom: "16px",
          borderLeft: `2px solid ${isUrgent ? alertColor : "var(--saffron)"}`,
          paddingLeft: "14px",
        }}>
          {urgent_message}
        </div>

        {/* Hora + Dasha pills row */}
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "14px" }}>
          {current_hora && (
            <div style={{
              background: "var(--bg2)",
              border: "1px solid var(--saffron)",
              borderRadius: "20px", padding: "6px 14px",
              fontSize: "1rem", color: "var(--saffron)",
              fontFamily: "'Inter',sans-serif",
              display: "flex", alignItems: "center", gap: "6px",
            }}>
              <span>{current_hora.icon}</span>
              <span>{(current_hora.ruler || "").charAt(0).toUpperCase() + (current_hora.ruler || "").slice(1)} hora until {current_hora.end}</span>
            </div>
          )}
          {dasha?.maha_planet && (
            <div style={{
              background: "var(--bg3)",
              border: "1px solid var(--border)",
              borderRadius: "20px", padding: "6px 14px",
              fontSize: "1rem", color: "var(--text-dim)",
              fontFamily: "'Inter',sans-serif",
              display: "flex", alignItems: "center", gap: "6px",
            }}>
              <span>{dasha.maha_emoji}</span>
              <span>{dasha.maha_planet.charAt(0).toUpperCase() + dasha.maha_planet.slice(1)} Dasha</span>
              {dasha.antar_planet && <><span>·</span><span>{dasha.antar_emoji} {dasha.antar_planet.charAt(0).toUpperCase() + dasha.antar_planet.slice(1)}</span></>}
            </div>
          )}
          {festival && (
            <div style={{
              background: "var(--bg2)", border: "1px solid var(--saffron)",
              borderRadius: "20px", padding: "6px 14px",
              fontSize: "1rem", color: "var(--saffron)",
              fontFamily: "'Inter',sans-serif",
            }}>
              🎉 {festival}
            </div>
          )}
        </div>

        {/* Remedy */}
        {remedy_today && (
          <div>
            <div style={{ fontSize:"0.95rem", fontFamily:"'Inter',sans-serif", color:"var(--saffron)", fontWeight:700, marginBottom:6 }}>
              🪷 Today's Remedy
            </div>
            <div style={{
              background: "var(--bg3)",
              border: "1px dashed var(--border)",
              borderRadius: "12px", padding: "14px 16px",
              fontSize: "1.2rem", color: "var(--text)",
              fontFamily: "'Inter',sans-serif",
              display: "flex", gap: "10px", alignItems: "flex-start",
              lineHeight: 1.7,
            }}>
              <span>{remedy_today}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Mini panchang card ────────────────────────────────────────────────────────
function PanchangCard({ onOpenPanchang, lang: langProp }) {
  const [data,    setData]    = useState(null);
  const [timings, setTimings] = useState(null);
  // Use global lang prop; fall back to localStorage for standalone use
  const lang = langProp || localStorage.getItem("panchang_lang") || "te";

  useEffect(() => {
    fetch(`${API}/panchang/today`)
      .then(r => r.json())
      .then(d => setData(d))
      .catch(() => {});
    fetch(`${API}/panchang/daily-timings`)
      .then(r => r.json())
      .then(d => setTimings(d))
      .catch(() => {});
  }, []);

  if (!data) return null;

  const L = PLANG[lang] || PLANG["en"];
  const vara = data.vara;
  const tithi = data.tithi;
  const nak = data.nakshatra;
  const festival = data.festival;

  const varaName  = lang === "te" ? vara.name_te  : L.vara[vara.index];
  const deityName = lang === "te" ? vara.deity_te : L.deity[vara.index];
  const tithiName = lang === "te" ? tithi.name_te : trHome(tithi.name_en, lang, TITHI_TRANS);
  const nakName   = lang === "te" ? nak.name_te   : trHome(nak.name_en,   lang, NAK_TRANS);

  return (
    <div style={{
      background: "var(--bg2)",
      border: "1px solid var(--border)",
      borderRadius: "14px", boxShadow: "var(--card-shadow)", marginBottom: "20px", overflow: "hidden",
    }}>
      {/* Clear section header */}
      <div style={{ padding:"12px 16px 10px", display:"flex", alignItems:"center", gap:10, borderBottom:"1px solid var(--border)" }}>
        <span style={{ fontSize:"1.1rem", width: 38, height: 38, borderRadius: "50%",
          background: "var(--bg3)", border: "1px solid var(--border)",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>📅</span>
        <div>
          <div style={{ fontFamily:"'Inter',sans-serif", fontSize:"0.95rem", color:"var(--saffron)", fontWeight:700 }}>Today's Panchang</div>
          <div style={{ fontSize:"0.85rem", color:"var(--text-dim)" }}>Vedic calendar · auspicious timings</div>
        </div>
      </div>

      {/* Card body */}
      <div onClick={onOpenPanchang} style={{
        padding: "10px 16px 12px", cursor: "pointer",
        display: "flex", alignItems: "center", gap: "14px",
      }}
        onMouseEnter={e => e.currentTarget.style.background = "var(--bg3)"}
        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
      >
        <div style={{
          background: "var(--bg2)", border: "1px solid var(--border)",
          borderRadius: "10px", padding: "8px 12px", flexShrink: 0, textAlign: "center",
        }}>
          <div style={{ fontSize: "0.95rem", fontWeight: 700, color: "var(--saffron)", fontFamily: "'Inter',sans-serif" }}>
            {varaName}
          </div>
          <div style={{ fontSize: "0.9rem", color: "var(--text-dim)", fontWeight: 500 }}>{deityName}</div>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {festival && (
            <div style={{ fontSize: "0.9rem", color: "var(--saffron)", marginBottom: "4px", fontWeight: 600 }}>
              🎉 {festival.name}
            </div>
          )}
          <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: "0.9rem", color: "var(--saffron)", fontWeight: 600 }}>{L.tithiLbl}</div>
              <div style={{ fontSize: "1.1rem", color: "var(--text)", fontFamily: "'Inter',sans-serif", fontWeight: 600 }}>
                {tithiName}
              </div>
            </div>
            <div>
              <div style={{ fontSize: "0.9rem", color: "var(--saffron)", fontWeight: 600 }}>{L.nakshatraLbl}</div>
              <div style={{ fontSize: "1.1rem", color: "var(--text)", fontFamily: "'Inter',sans-serif", fontWeight: 600 }}>
                {nakName}
              </div>
            </div>
          </div>
        </div>

        <div style={{ flexShrink: 0 }}>
          <div style={{ color: "var(--saffron)", fontSize: "1.2rem" }}>›</div>
        </div>
      </div>

      {/* Rahu Kalam + Sunrise strip */}
      {timings && (() => {
        const TL = { te:{sunrise:"సూర్యోదయం",rahu:"రాహు కాలం",yama:"యమగంఢం"}, hi:{sunrise:"सूर्योदय",rahu:"राहु काल",yama:"यमगण्ड"}, ta:{sunrise:"சூரிய உதயம்",rahu:"ராகு காலம்",yama:"யமகண்டம்"}, kn:{sunrise:"ಸೂರ್ಯೋದಯ",rahu:"ರಾಹು ಕಾಲ",yama:"ಯಮಗಂಡ"}, mr:{sunrise:"सूर्योदय",rahu:"राहु काल",yama:"यमगंड"}, en:{sunrise:"Sunrise",rahu:"Rahu Kalam",yama:"Yamagandam"} };
        const L = TL[lang] || TL.te;
        return (
          <div style={{ borderTop:"1px solid var(--border)", padding:"8px 16px 10px", display:"flex", gap:"12px", flexWrap:"wrap" }}>
            <div style={{ display:"flex", alignItems:"center", gap:"5px" }}>
              <span style={{ fontSize:"0.9rem" }}>🌅</span>
              <span style={{ fontSize:"0.9rem", color:"var(--text-dim)" }}>{L.sunrise} {timings.sunrise}</span>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:"5px" }}>
              <span style={{ fontSize:"0.9rem" }}>🚫</span>
              <span style={{ fontSize:"0.9rem", color:"#b91c1c", fontWeight:600 }}>{L.rahu} {timings.rahu_kalam.start}–{timings.rahu_kalam.end}</span>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:"5px" }}>
              <span style={{ fontSize:"0.9rem" }}>⚠️</span>
              <span style={{ fontSize:"0.9rem", color:"#b45309", fontWeight:600 }}>{L.yama} {timings.yamagandam.start}–{timings.yamagandam.end}</span>
            </div>
          </div>
        );
      })()}
      {/* Clear CTA — open full calendar */}
      <div style={{ borderTop:"1px solid var(--border)", padding:"8px 16px 10px" }}>
        <button onClick={onOpenPanchang} style={{
          width:"100%", background:"var(--bg2)", border:"1px solid var(--saffron)",
          borderRadius:10, padding:"9px 16px", cursor:"pointer",
          display:"flex", alignItems:"center", justifyContent:"center", gap:6,
          color:"var(--saffron)", fontFamily:"'Inter',sans-serif", fontSize:"0.9rem",
          fontWeight:700,
          transition:"opacity 0.2s",
        }}
          onMouseEnter={e => { e.currentTarget.style.opacity="0.75"; }}
          onMouseLeave={e => { e.currentTarget.style.opacity="1"; }}
        >
          🗓 View Full Calendar ›
        </button>
      </div>
    </div>
  );
}

// ── Language preference card shown on HomePage ───────────────────────────────
const LANG_OPTIONS = [
  { code: "en", label: "English",  chip: "EN",  nativeChip: "EN"  },
  { code: "hi", label: "Hindi",    chip: "हि",   nativeChip: "हिन्दी" },
  { code: "te", label: "Telugu",   chip: "తె",   nativeChip: "తెలుగు" },
  { code: "ta", label: "Tamil",    chip: "த",   nativeChip: "தமிழ்"  },
  { code: "kn", label: "Kannada",  chip: "ಕ",   nativeChip: "ಕನ್ನಡ" },
  { code: "mr", label: "Marathi",  chip: "म",   nativeChip: "मराठी"  },
];

const LANG_CHIPS_HOME = { en:"EN", hi:"हि", te:"తె", ta:"த", kn:"ಕ", mr:"म" };

function LanguageSettingsCard({ appLang, appScript, onLangChange }) {
  const showScript = appLang !== "en";
  const chip       = LANG_CHIPS_HOME[appLang] || "EN";

  return (
    <>
    <style>{`@media(max-width:640px){.vs-lang-bar{margin:-16px -16px 0 -16px !important;}}`}</style>
    <div className="vs-lang-bar" style={{
      background: "var(--bg2)",
      borderBottom: "1px solid var(--border)",
      margin: "-32px -32px 0 -32px",
      padding: "10px 20px",
      display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
    }}>

      {/* Language chips */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {["en","hi","te","ta","kn","mr"].map(l => (
          <button key={l}
            onClick={() => onLangChange(l, l === "en" ? "native" : appScript)}
            style={{
              background: appLang === l ? "var(--saffron)" : "var(--bg3)",
              color:      appLang === l ? "#0f0e0d"        : "var(--text-dim)",
              border:     `1px solid ${appLang === l ? "var(--saffron)" : "var(--border)"}`,
              borderRadius: 20, padding: "5px 14px",
              fontFamily: "'Inter',sans-serif", fontSize: "0.95rem",
              fontWeight: appLang === l ? 700 : 400,
              cursor: "pointer", transition: "all 0.15s",
            }}>
            {LANG_CHIPS_HOME[l]}
          </button>
        ))}
      </div>

      {/* Script toggle — only for non-English, same style as JyotishPage */}
      {showScript && (
        <div style={{ display: "flex", borderRadius: 10, overflow: "hidden",
          border: "1px solid var(--border)", marginLeft: "auto" }}>
          {[["roman","Aa"], ["native", chip]].map(([val, label]) => (
            <button key={val}
              onClick={() => onLangChange(appLang, val)}
              style={{
                padding: "5px 14px", fontSize: "0.95rem", border: "none", cursor: "pointer",
                background: appScript === val ? "rgba(232,131,42,0.18)" : "transparent",
                color:      appScript === val ? "var(--saffron)"         : "var(--text-dim)",
                transition: "all 0.15s", fontFamily: val === "native" ? "inherit" : "sans-serif",
              }}>
              {label}
            </button>
          ))}
        </div>
      )}

    </div>
    </>
  );
}

export default function HomePage({ user, onNavigate, onAskGita, onOpenCompanion, appLang, appScript, onLangChange }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  // fall back to localStorage if props not yet provided
  const effectiveLang   = appLang   || localStorage.getItem("panchang_lang") || "en";
  const effectiveScript = appScript || localStorage.getItem("jyotish_script_pref") || "native";

  useEffect(() => {
    fetch(`${API}/home/${encodeURIComponent(user.id)}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [user.id]);

  const streak = data?.journey?.streak || 0;
  // "gita" = user opted out of Vedic astrology content on the home page (see Profile settings)
  const showAstro = !loading && data?.content_focus !== "gita";

  return (
    <div className="page" style={{ maxWidth: 1050 }}>

      {/* Language bar — flush top, edge-to-edge, before hero */}
      {onLangChange && (
        <LanguageSettingsCard
          appLang={effectiveLang}
          appScript={effectiveScript}
          onLangChange={onLangChange}
        />
      )}

      {/* Hero header */}
      <HeroSection user={user} />

      {/* 2-column grid — single column on mobile */}
      <div style={{ display: "flex", gap: "24px", alignItems: "flex-start",
        flexDirection: window.innerWidth < 700 ? "column" : "row" }}>

        {/* ── Left column: main content ── */}
        <div style={{ flex: "1 1 0", minWidth: 0, width: "100%" }}>

          {/* Daily personalized briefing — most important card */}
          {showAstro && (
            <DailyBriefingCard
              userId={user.id}
              lang={effectiveLang}
              scriptPref={effectiveScript}
              onOpenJyotish={() => onNavigate("jyotish")}
            />
          )}

          {/* Daily panchang strip */}
          {showAstro && <PanchangCard onOpenPanchang={() => onNavigate("panchang")} lang={effectiveLang} />}

          {/* Daily rashi horoscope teaser */}
          {showAstro && (
            <RashiHoroscopeTeaser
              userId={user.id}
              onOpenJyotish={() => onNavigate("jyotish")}
            />
          )}

          {/* Loading */}
          {loading && (
            <>
              <Skeleton h={200} />
              <Skeleton h={140} />
            </>
          )}

          {!loading && data && (
            <>
              {/* Journey — primary card */}
              <JourneyHeroCard
                journey={data.journey}
                onGoToJourney={() => onNavigate("journey")}
              />

              {/* Ask the Gita */}
              {/* WhatsApp agent banner */}
              <a
                href="https://wa.me/15556469286?text=Namaste"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "flex", alignItems: "center", gap: "14px",
                  background: "#F0FBF6",
                  border: "1px solid #25D366",
                  borderRadius: "16px", padding: "16px 20px",
                  marginBottom: "20px", textDecoration: "none",
                  boxShadow: "0 2px 12px rgba(37,211,102,0.12)",
                  transition: "box-shadow 0.2s",
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 20px rgba(37,211,102,0.22)"; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 2px 12px rgba(37,211,102,0.12)"; }}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="#25D366" style={{ flexShrink: 0 }}>
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                <div style={{ flex: 1 }}>
                  <div style={{ color: "#0C5A3E", fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "0.98rem", marginBottom: "3px" }}>
                    Ask Krishna on WhatsApp
                  </div>
                  <div style={{ color: "#3A7A63", fontSize: "0.9rem" }}>
                    Daily verse · Panchang · Gita guidance — no app needed
                  </div>
                </div>
                <div style={{ color: "#0C5A3E", fontSize: "1.2rem" }}>→</div>
              </a>

              {/* AI Pandit free trial */}
              {showAstro && <AIPanditCard user={user} onOpenJyotish={() => onNavigate("jyotish")} />}

              {/* Community Stories */}
              <StoriesFeed />
            </>
          )}

          {!loading && !data && (
            <div style={{ textAlign: "center", padding: "60px 20px" }}>
              <div style={{ fontSize: "3rem", marginBottom: "16px", opacity: 0.4 }}>ॐ</div>
              <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, color: "var(--text)", marginBottom: "8px" }}>
                Something went wrong
              </div>
              <div style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>
                Could not load content. Please refresh the page.
              </div>
            </div>
          )}
        </div>

        {/* ── Right column: stats & streak ── */}
        <div style={{ flex: window.innerWidth < 700 ? "none" : "0 0 280px",
          width: window.innerWidth < 700 ? "100%" : 280 }}>

          {/* Streak */}
          {streak > 0 && <StreakCard streak={streak} />}

          {/* Stats */}
          {!loading && data && (
            <StatsStrip
              totalConvs={data.total_conversations}
              daysOnJourney={data.journey?.days_done || 0}
            />
          )}
          {loading && <Skeleton h={90} />}

          {/* Weekly insight */}
          {!loading && data?.weekly_insight && (
            <WeeklyInsightCard
              insight={data.weekly_insight}
              onOpenCompanion={() => onOpenCompanion?.(data.journey)}
            />
          )}
        </div>

      </div>
    </div>
  );
}
